import React, { useState } from "react"
import PropTypes from "prop-types"
import { ThemeProvider, mediaQuery } from "@zendeskgarden/react-theming"
import styled, { createGlobalStyle } from "styled-components"
import { Helmet } from "react-helmet"
import { mediaStyles } from "../lib/media-queries"
import { collections } from "../lib/prop-types"
import { lightTheme, darkTheme } from "../styles/themes"
import "@zendeskgarden/css-bedrock"
import Header from "./header/header"
import usePersistentSidebarScroll from "../lib/hooks/usePersistentSidebarScroll"
import useFocus from "../lib/hooks/useFocus"

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${props => props.theme.colors.background};
  }
`

const Container = styled.div`
  display: grid;
  grid-template-areas:
    "header header"
    "nav main";
  grid-template-rows: auto 1fr;
  grid-template-columns: ${props =>
    `${props.theme.layout.leftColumn.width} 1fr`};
  height: 100vh;

  ${props => mediaQuery("down", "md", props.theme)} {
    grid-template-areas:
      "header"
      "nav"
      "main";
    grid-template-rows: auto max-content 1fr;
    grid-template-columns: 1fr;
  }
`

const HeaderRow = styled.div`
  grid-area: header;
`

const Navigation = styled.aside`
  grid-area: nav;
  overflow-y: scroll;
`

const Main = styled.main`
  grid-area: main;
  overflow-y: scroll;
`

const Layout = ({ collection, navigation, children }) => {
  usePersistentSidebarScroll()
  const targetRef = useFocus()
  const collectionDefaultTheme =
    collection === "api-reference" ? "dark" : "light"
  const [theme, setTheme] = useState(collectionDefaultTheme)

  const toggleTheme = () => {
    if (theme === "light") {
      setTheme("dark")
    } else {
      setTheme("light")
    }
  }

  if (typeof window !== "undefined") {
    window.toggleTheme = toggleTheme // eslint-disable-line no-undef
  }

  return (
    <>
      <Helmet>
        <style type="text/css">{mediaStyles}</style>
      </Helmet>
      <ThemeProvider theme={theme === "light" ? lightTheme : darkTheme}>
        <GlobalStyle />
        <Container data-testid="dev-docs-layout-container">
          <HeaderRow>
            <Header collection={collection} />
          </HeaderRow>
          <Navigation data-testid="dev-docs-layout-navigation">
            {navigation}
          </Navigation>
          <Main
            ref={targetRef}
            data-testid="dev-docs-layout-main"
            tabIndex={-1}
          >
            {children}
          </Main>
        </Container>
      </ThemeProvider>
    </>
  )
}

Layout.propTypes = {
  collection: collections,
  navigation: PropTypes.element.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
}

Layout.defaultProps = {
  collection: "documentation"
}

export default Layout
